import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Paper, Divider, Chip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement
} from 'chart.js';
import { Doughnut, Pie, Bar, Line } from 'react-chartjs-2';
import api from '../../services/api';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement
);

const DashboardHome = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const roles = (user.roles || []).map(r => r.toLowerCase());
  const isAdmin = roles.some(r => ['admin', 'sys_admin', 'system administrator', 'system admin'].includes(r));

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard-stats');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#10b981' }} />
      </Box>
    );
  }

  const allStats = isAdmin ? (data?.admin || []) : (data?.biz || []);
  const stats = allStats.filter(stat => Number(stat.value || 0) > 0);

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
        case 'high': return '#ef4444';
        case 'medium': return '#f59e0b';
        case 'low': return '#10b981';
        default: return '#94a3b8';
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#94a3b8', font: { size: 10 }, usePointStyle: true, padding: 15 }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        padding: 12,
        displayColors: true
      }
    }
  };

  const barOptions = {
    ...chartOptions,
    indexAxis: 'x',
    scales: {
      y: { 
        grid: { color: 'rgba(255,255,255,0.05)' }, 
        border: { display: false }, 
        ticks: { 
          color: '#64748b', 
          font: { size: 10 },
          callback: (value) => `${value}%`
        },
        max: 100,
        min: 0
      },
      x: { grid: { display: false }, border: { display: false }, ticks: { color: '#64748b', font: { size: 10 } } }
    }
  };

  const horizontalBarOptions = {
    ...barOptions,
    indexAxis: 'y',
    plugins: {
        ...chartOptions.plugins,
        legend: { display: false }
    }
  };

  const colorMap = {
    // Ratings
    'extreme': '#b91c1c', 
    'high': '#ef4444', 
    'tolerable': '#fbbf24', 
    'moderate': '#fbbf24',
    'medium': '#fbbf24',
    'low': '#10b981', 
    'very low': '#064e3b',
    // Statuses
    'open': '#ef4444',
    'resolved': '#10b981',
    'closed': '#64748b',
    'in progress': '#f59e0b',
    'pending': '#3b82f6',
    'completed': '#10b981'
  };

  const hasChartData = (apiData) => Array.isArray(apiData) && apiData.length > 0;

  const NoData = () => (
    <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
      No data available
    </Box>
  );

  const prepareChartData = (apiData, label, defaultColors) => {
    if (!hasChartData(apiData)) return null;
    
    // Use semantic colors if labels match colorMap
    const backgroundColors = apiData.map(d => {
        const key = String(d.label).toLowerCase().trim();
        return colorMap[key] || null;
    });

    // Fallback to defaultColors or standard palette for any nulls
    const palette = Array.isArray(defaultColors)
      ? defaultColors
      : defaultColors
        ? [defaultColors]
        : ['#3b82f6', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899'];
    const finalColors = backgroundColors.map((c, i) => c || palette[i % palette.length]);

    return {
      labels: apiData.map(d => d.label),
      datasets: [{
        label: label,
        data: apiData.map(d => d.value),
        backgroundColor: finalColors,
        borderWidth: 0,
        hoverOffset: 4
      }]
    };
  };

  const permissions = user.permissions || [];
  const canViewSHE = permissions.some(p => p.section_name === 'SHE Compliance' && p.can_view === 1);
  const canViewRisks = permissions.some(p => (p.section_name === 'Risk Registers' || p.section_name === 'Risk Assessment') && p.can_view === 1);
  
  const isSheRole = roles.some(r => ['she_manager', 'she_officer', 'she manager', 'she officer'].includes(r));
  const isRiskRole = roles.some(r => ['risk_manager', 'risk_officer', 'risk_owner', 'risk_champion', 'risk_admin', 'manager', 'director'].includes(r));
  const isDirector = roles.includes('director');

  const showSheCharts = isAdmin || canViewSHE || isSheRole || isDirector || isRiskRole;
  const showRiskCharts = isAdmin || canViewRisks || isRiskRole || isDirector;

  console.log('Dashboard Auth Debug:', { 
    roles, 
    isAdmin, 
    canViewSHE, 
    canViewRisks, 
    isSheRole, 
    isRiskRole, 
    showSheCharts, 
    showRiskCharts 
  });

  const ChartCard = ({ title, children, height = 300 }) => (
    <Paper className="glass" sx={{ p: 3, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ color: 'white', fontWeight: 'bold', mb: 3, fontSize: '1rem' }}>{title}</Typography>
        <Box sx={{ height: height, width: '100%', position: 'relative' }}>
            {children}
        </Box>
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold', mb: 4 }}>
        Risk & Compliance
      </Typography>

      <Box className="dashboard-grid" sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(auto-fill, minmax(240px, 1fr))' },
        gap: '24px', 
        mb: 4 
      }}>
        {stats.map((stat, index) => (
          <Paper key={index} className="glass" sx={{ p: 3, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)' }}>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', mb: 1, fontWeight: '500' }}>{stat.label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: 'white', fontSize: '2rem', fontWeight: 'bold' }}>{stat.value}</Typography>
                <Chip 
                    label={stat.trend} 
                    size="small" 
                    sx={{ 
                        background: 'rgba(16, 185, 129, 0.1)', 
                        color: stat.color || '#10b981', 
                        fontSize: '0.7rem', 
                        fontWeight: 'bold',
                        border: stat.color ? `1px solid ${stat.color}40` : '1px solid rgba(16, 185, 129, 0.4)'
                    }} 
                />
            </Box>
            <Box sx={{ height: '4px', width: '40%', background: stat.color || '#10b981', borderRadius: '2px', mt: 2, opacity: 0.6 }}></Box>
          </Paper>
        ))}
      </Box>

      {/* Visual Analytics Section */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
          gap: '24px' 
        }}>
          
          {showSheCharts && (
            <>
              {hasChartData(data?.charts?.she_by_category) && (
                <ChartCard title="SHE Activities by Category">
                  <Doughnut data={prepareChartData(data.charts.she_by_category, 'Events')} options={chartOptions} />
                </ChartCard>
              )}
              
              {hasChartData(data?.charts?.she_by_status) && (
                <ChartCard title="Compliance Status Distribution">
                  <Pie data={prepareChartData(data.charts.she_by_status, 'Events', ['#10b981', '#f59e0b', '#ef4444', '#3b82f6'])} options={chartOptions} />
                </ChartCard>
              )}
            </>
          )}

          {showRiskCharts && (
            <>
              <ChartCard title="Risk Mitigation Performance (By Category)">
                {hasChartData(data?.charts?.risk_comparison) ? (
                  <Bar 
                    data={{
                      labels: data.charts.risk_comparison.map(d => d.label),
                      datasets: [
                        {
                          label: 'Inherent Risk (%)',
                          data: data.charts.risk_comparison.map(d => ((parseFloat(d.inherent) || 0) / 25 * 100).toFixed(1)),
                          backgroundColor: 'rgba(239, 68, 68, 0.7)',
                          borderRadius: 4
                        },
                        {
                          label: 'Residual Risk (%)',
                          data: data.charts.risk_comparison.map(d => ((parseFloat(d.residual) || 0) / 25 * 100).toFixed(1)),
                          backgroundColor: '#10b981',
                          borderRadius: 4
                        }
                      ]
                    }} 
                    options={barOptions} 
                  />
                ) : <NoData />}
              </ChartCard>

              <ChartCard title="Portfolio Risk Exposure (Total Portfolio %)">
                {Number(data?.biz?.find(s => s.label === 'Total Risks')?.value || 0) > 0 && data?.charts?.portfolio_stats ? (() => {
                  const totalPossibleScore = (data.biz?.find(s => s.label === 'Total Risks')?.value || 0) * 25;
                  const inherentPercent = totalPossibleScore > 0 ? (data.charts.portfolio_stats.totalInherent / totalPossibleScore * 100).toFixed(1) : 0;
                  const residualPercent = totalPossibleScore > 0 ? (data.charts.portfolio_stats.totalResidual / totalPossibleScore * 100).toFixed(1) : 0;
                  
                  return (
                    <Bar 
                        data={{
                        labels: ['Overall Portfolio Exposure'],
                        datasets: [
                            {
                            label: 'Total Inherent (%)',
                            data: [inherentPercent],
                            backgroundColor: 'rgba(239, 68, 68, 0.9)',
                            barThickness: 60,
                            borderRadius: 12
                            },
                            {
                            label: 'Total Residual (%)',
                            data: [residualPercent],
                            backgroundColor: '#10b981',
                            barThickness: 60,
                            borderRadius: 12
                            }
                        ]
                        }} 
                        options={{
                            ...barOptions,
                            scales: {
                                ...barOptions.scales,
                                x: { ...barOptions.scales.x, stacked: false }
                            }
                        }} 
                    />
                  );
                })() : <NoData />}
              </ChartCard>

              <ChartCard title="Total Risks by Status Distribution">
                {hasChartData(data?.charts?.risks_by_status) ? (
                  <Pie data={prepareChartData(data.charts.risks_by_status, 'Risks')} options={chartOptions} />
                ) : <NoData />}
              </ChartCard>

              <ChartCard title="Residual Risk Ratings">
                {hasChartData(data?.charts?.risks_by_level) ? (
                  <Doughnut data={prepareChartData(data.charts.risks_by_level, 'Risks')} options={chartOptions} />
                ) : <NoData />}
              </ChartCard>
            </>
          )}

          {isAdmin && (
            <ChartCard title="User Status Analytics">
              <Bar 
                data={{
                  labels: ['Active', 'Inactive', 'Admin', 'Standard'],
                  datasets: [{
                    label: 'Users',
                    data: [data?.admin?.[0]?.value || 0, 0, 2, 5],
                    backgroundColor: ['#10b981', '#ef4444', '#3b82f6', '#f59e0b'],
                    borderRadius: 8
                  }]
                }} 
                options={barOptions} 
              />
            </ChartCard>
          )}

          {isDirector && (
            <>
              <ChartCard title="Risks by Department (Top 5)">
                {hasChartData(data?.charts?.risks_by_department) ? (
                  <Bar 
                    data={prepareChartData(data.charts.risks_by_department, 'Risks', '#ef4444')} 
                    options={horizontalBarOptions} 
                  />
                ) : <NoData />}
              </ChartCard>

              <ChartCard title="SHE Events by Department (Top 5)">
                {hasChartData(data?.charts?.she_by_department) ? (
                  <Bar 
                    data={prepareChartData(data.charts.she_by_department, 'Events', '#10b981')} 
                    options={horizontalBarOptions} 
                  />
                ) : <NoData />}
              </ChartCard>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>
        {/* Recent SHE Activities */}
        <Paper className="glass" sx={{ p: 3, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography sx={{ color: 'white', fontWeight: 'bold' }}>Recent SHE Compliance Activities</Typography>
          </Box>
          <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.05)' }} />
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <th style={{ padding: '12px', color: '#64748b', fontSize: '0.75rem' }}>Action ID</th>
                  <th style={{ padding: '12px', color: '#64748b', fontSize: '0.75rem' }}>Category</th>
                  <th style={{ padding: '12px', color: '#64748b', fontSize: '0.75rem' }}>Priority</th>
                  <th style={{ padding: '12px', color: '#64748b', fontSize: '0.75rem' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.recent_she?.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                    <td style={{ padding: '12px', color: '#10b981', fontWeight: 'bold', fontSize: '0.85rem' }}>{item.action_id}</td>
                    <td style={{ padding: '12px', color: '#f1f5f9', fontSize: '0.85rem' }}>{item.activity_category}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ color: getPriorityColor(item.priority), fontSize: '0.75rem', fontWeight: 'bold' }}>{item.priority}</span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Chip label={item.status} size="small" sx={{ height: '20px', fontSize: '0.65rem', background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }} />
                    </td>
                  </tr>
                ))}
                {(!data?.recent_she || data.recent_she.length === 0) && (
                    <tr>
                        <td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: '#475569' }}>No recent compliance events.</td>
                    </tr>
                )}
              </tbody>
            </table>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default DashboardHome;
